// --- src/context/AuthContext.js ---
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { logoutUser as apiLogout, apiClient } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [isAuthenticated, setIsAuthenticated] = useState(!!authToken);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Renamed to localFetchUserDetails to avoid conflict if passed in value
  const localFetchUserDetails = useCallback(async (tokenForFetch) => {
    if (tokenForFetch) {
      // Add Authorization header for this specific request
      const currentApiClientConfig = { ...apiClient.defaults.headers };
      apiClient.defaults.headers.common['Authorization'] = `Token ${tokenForFetch}`;
      
      try {
        const response = await apiClient.get('auth/user/');
        setCurrentUser(response.data);
        console.log("AuthContext: User details fetched", response.data);
      } catch (error) {
        console.error("AuthContext: Failed to fetch user details:", error.response?.data || error.message);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            console.log("AuthContext: Token might be invalid during fetchUserDetails, logging out.");
            localStorage.removeItem('authToken');
            setAuthToken(null);
            setIsAuthenticated(false);
            setCurrentUser(null);
            delete apiClient.defaults.headers.common['Authorization']; // Clean up header
        }
      } finally {
        // Restore original apiClient headers if they were changed
         apiClient.defaults.headers.common = currentApiClientConfig.common || {};
         // Or more specifically:
         if (!localStorage.getItem('authToken')) { // if logged out by error above
            delete apiClient.defaults.headers.common['Authorization'];
         } else { // if still logged in, ensure Authorization header is set from localStorage for subsequent requests
            apiClient.defaults.headers.common['Authorization'] = `Token ${localStorage.getItem('authToken')}`;
         }
      }
    } else {
        setCurrentUser(null);
    }
  }, []); // No dependencies needed that would cause re-creation loops

  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);
    const tokenFromStorage = localStorage.getItem('authToken');
    if (tokenFromStorage) {
      setAuthToken(tokenFromStorage);
      setIsAuthenticated(true);
      // Set Authorization header for apiClient for this session
      apiClient.defaults.headers.common['Authorization'] = `Token ${tokenFromStorage}`;
      await localFetchUserDetails(tokenFromStorage);
    } else {
      setAuthToken(null);
      setIsAuthenticated(false);
      setCurrentUser(null);
      delete apiClient.defaults.headers.common['Authorization'];
    }
    setIsLoading(false);
  }, [localFetchUserDetails]);

  useEffect(() => {
    checkAuthStatus();
    const handleStorageChange = (event) => {
        if (event.key === 'authToken') {
            const currentTokenInStorage = localStorage.getItem('authToken');
            if (authToken !== currentTokenInStorage) {
                checkAuthStatus();
            }
        }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAuthStatus, authToken]);

  const login = (token, userData) => {
    localStorage.setItem('authToken', token);
    apiClient.defaults.headers.common['Authorization'] = `Token ${token}`;
    setAuthToken(token);
    setIsAuthenticated(true);
    if (userData) {
      setCurrentUser(userData);
    } else {
      localFetchUserDetails(token);
    }
  };

  const logout = async () => {
    try {
      if (authToken) { // Only call API logout if there was a token
        await apiLogout();
      }
    } catch (error) {
      console.error("AuthContext: Logout API call failed, proceeding with local logout:", error);
    } finally {
      localStorage.removeItem('authToken');
      delete apiClient.defaults.headers.common['Authorization'];
      setAuthToken(null);
      setIsAuthenticated(false);
      setCurrentUser(null);
    }
  };

  return (
    // Expose localFetchUserDetails as fetchUserDetails for AccountPage to use
    <AuthContext.Provider value={{ authToken, isAuthenticated, isLoading, currentUser, login, logout, fetchUserDetails: localFetchUserDetails }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
